import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { api, errMsg, fmtDate, todayStr, periodRange, shiftPeriod, rangeDays, wd, LOCALES } from "@/lib/api";
import { Card, Loader, PageTitle } from "@/components/kv";

export const ModeToggle = ({ mode, setMode, prefix }) => {
  const { t } = useT();
  return (
    <div className="flex rounded-full border border-[#E8DFD0] bg-white p-0.5">
      {["week", "month"].map((m) => (
        <button key={m} onClick={() => setMode(m)} data-testid={`${prefix}-mode-${m}`}
          className={`px-4 py-1.5 text-sm rounded-full transition-colors ${mode === m ? "bg-[#b8563a] text-white" : "text-stone-600"}`}>
          {t(`common.${m}`)}
        </button>
      ))}
    </div>
  );
};

export const PeriodNav = ({ mode, anchor, setAnchor, prefix }) => {
  const { t, lang } = useT();
  const [s, e] = periodRange(mode, anchor);
  const label = mode === "month"
    ? new Intl.DateTimeFormat(LOCALES[lang], { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${s}T00:00:00Z`))
    : `${fmtDate(s, lang, { day: "numeric", month: "short" })} – ${fmtDate(e, lang, { day: "numeric", month: "short" })}`;
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="icon" className="rounded-full" onClick={() => setAnchor(shiftPeriod(mode, anchor, -1))} data-testid={`${prefix}-prev`} aria-label={t("common.prev")}><ChevronLeft className="h-4 w-4" /></Button>
      <span className="min-w-[150px] text-center text-sm font-semibold capitalize" data-testid={`${prefix}-label`}>{label}</span>
      <Button variant="outline" size="icon" className="rounded-full" onClick={() => setAnchor(shiftPeriod(mode, anchor, 1))} data-testid={`${prefix}-next`} aria-label={t("common.next")}><ChevronRight className="h-4 w-4" /></Button>
    </div>
  );
};

const entryLabel = (entry, t) =>
  entry.type === "shift" ? `${entry.start}–${entry.end}` : entry.type === "leave" ? t(`leaves.types.${entry.leave_type}`) : t("shift.off");

const entryTone = (entry) =>
  entry.type === "shift" ? "bg-white" : entry.type === "leave" ? "bg-violet-50 text-violet-800" : "bg-[#F3EDE2] text-stone-500";

export default function StaffSchedule() {
  const { t, lang } = useT();
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(todayStr());
  const [days, setDays] = useState(null);
  const [s, e] = periodRange(mode, anchor);
  const today = todayStr();

  useEffect(() => {
    setDays(null);
    api.get("/me/schedule", { params: { start: s, end: e } }).then((r) => setDays(r.data.days)).catch((err) => toast.error(errMsg(err, t)));
  }, [s, e, t]);

  const list = rangeDays(s, e);
  return (
    <div>
      <PageTitle kicker={t("app.name")} title={t("staffnav.schedule")} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <ModeToggle mode={mode} setMode={setMode} prefix="my-schedule" />
        <PeriodNav mode={mode} anchor={anchor} setAnchor={setAnchor} prefix="my-schedule" />
      </div>
      {!days || !list.every((ds) => days[ds]) ? <Loader /> : mode === "week" ? (
        <div className="space-y-2" data-testid="my-schedule-week">
          {list.map((ds) => (
            <Card key={ds} className={`p-4 flex items-center justify-between ${ds === today ? "ring-2 ring-[#c86d51]" : ""} ${entryTone(days[ds])}`} data-testid={`my-day-${ds}`}>
              <span className="font-semibold capitalize">{fmtDate(ds, lang, { weekday: "long", day: "numeric", month: "short" })}</span>
              <span className="font-mono-kv text-sm">{entryLabel(days[ds], t)}{days[ds].custom && " ★"}</span>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1.5" data-testid="my-schedule-month">
          {t("days").map((d) => <div key={d} className="text-center text-xs font-semibold text-stone-500 py-1">{d}</div>)}
          {Array.from({ length: wd(s) }).map((_, i) => <div key={`b${i}`} />)}
          {list.map((ds) => (
            <div key={ds} className={`rounded-lg border border-[#E8DFD0] p-1.5 min-h-[64px] text-[10px] sm:text-xs ${entryTone(days[ds])} ${ds === today ? "ring-2 ring-[#c86d51]" : ""}`} data-testid={`my-day-${ds}`}>
              <div className="font-bold text-sm">{Number(ds.slice(8))}</div>
              <div className="font-mono-kv leading-tight">
                {days[ds].type === "shift" ? <>{days[ds].start}<br />{days[ds].end}</> : t(days[ds].type === "leave" ? "shift.leave_s" : "shift.off_s")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
